import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Search, X } from "lucide-react";
import { countWhatsAppMatches } from "../../utils/whatsappFormat";
import { cn } from "../../utils/cn";

// ThreadSearch — búsqueda DENTRO del hilo abierto.
//
// Es client-side sobre los mensajes ya cargados (instantánea, sin red): el
// endpoint /conversations/search del contrato es para la búsqueda global de la
// lista, no para esto.
//
// El conteo usa countWhatsAppMatches con el MISMO `format` con que se pinta
// cada mensaje, así el "3 de 12" nunca se desalinea con los <mark> visibles.

/**
 * Estado de la búsqueda del hilo.
 * @param {object}   params
 * @param {Array}    params.messages       - mensajes cargados del hilo
 * @param {Function} params.getSearchable  - (msg) => { text, format } | null  (debe ser estable)
 * @param {object}   params.rootRef        - ref al contenedor del hilo (para el atajo Ctrl/Cmd+F)
 * @param {object}   [params.toggleRef]    - ref al botón que abre la barra (destino del foco al cerrar)
 */
export function useThreadSearch({ messages, getSearchable, rootRef, toggleRef }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  const term = query.trim();

  const { index, total } = useMemo(() => {
    const map = new Map();
    let running = 0;
    if (term) {
      for (const msg of messages || []) {
        const searchable = getSearchable(msg);
        if (!searchable || !searchable.text) continue;
        const count = countWhatsAppMatches(searchable.text, term, searchable.format);
        if (count > 0) {
          map.set(msg.id, { base: running, count });
          running += count;
        }
      }
    }
    return { index: map, total: running };
  }, [messages, term, getSearchable]);

  // Volver a la primera coincidencia al cambiar el término; y no quedar fuera
  // de rango cuando llegan mensajes nuevos por SSE y el total baja.
  useEffect(() => {
    setActive(0);
  }, [term]);
  useEffect(() => {
    setActive((prev) => (total === 0 ? 0 : Math.min(prev, total - 1)));
  }, [total]);

  const activeMessageId = useMemo(() => {
    if (!total) return null;
    for (const [id, entry] of index) {
      if (active >= entry.base && active < entry.base + entry.count) return id;
    }
    return null;
  }, [index, active, total]);

  const matchBaseFor = useCallback((id) => index.get(id)?.base ?? 0, [index]);
  const hasMatchesIn = useCallback((id) => index.has(id), [index]);

  const goNext = useCallback(() => {
    setActive((prev) => (total ? (prev + 1) % total : 0));
  }, [total]);
  const goPrev = useCallback(() => {
    setActive((prev) => (total ? (prev - 1 + total) % total : 0));
  }, [total]);

  const openSearch = useCallback(() => {
    setOpen(true);
    // El input se monta en el mismo tick: el foco va tras el commit.
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  }, []);

  // Cierre silencioso: la barra desaparece sin tocar el foco. Es el que usa el
  // cambio de conversación, donde el foco ya pertenece a la lista o al hilo nuevo.
  const resetSearch = useCallback(() => {
    setOpen(false);
    setQuery("");
    setActive(0);
  }, []);

  // Cierre desde la UI (Esc o la X): al desmontarse la barra el foco caería al
  // <body> y el usuario de teclado volvería al principio del documento, así que
  // se devuelve al botón que la abrió.
  const closeSearch = useCallback(() => {
    resetSearch();
    toggleRef?.current?.focus();
  }, [resetSearch, toggleRef]);

  // Ctrl/Cmd+F: solo se roba el buscador del navegador cuando el foco vive
  // dentro del hilo. Si el usuario está en cualquier otra parte de la app, el
  // atajo nativo sigue funcionando.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== "f") return;
      const root = rootRef?.current;
      if (!root || !document.activeElement || !root.contains(document.activeElement)) return;
      e.preventDefault();
      openSearch();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [rootRef, openSearch]);

  // Cerrar y limpiar al cambiar de conversación lo maneja el padre vía key/efecto.
  return {
    open,
    query,
    setQuery,
    term,
    total,
    active,
    ordinal: total ? active + 1 : 0,
    activeMessageId,
    matchBaseFor,
    hasMatchesIn,
    goNext,
    goPrev,
    openSearch,
    closeSearch,
    resetSearch,
    inputRef,
  };
}

// Id del contenedor: el botón que despliega la barra lo referencia con
// aria-controls, para que el lector de pantalla pueda llevar al usuario a lo
// que acaba de anunciar como "expandido".
export const THREAD_SEARCH_ID = "helpdesk-thread-search";

/**
 * Barra de búsqueda del hilo. Se despliega bajo la fila de identidad del header.
 */
export default function ThreadSearch({
  query,
  onQueryChange,
  total,
  ordinal,
  onNext,
  onPrev,
  onClose,
  inputRef,
}) {
  const { t } = useTranslation();
  const hasTerm = query.trim().length > 0;
  const empty = hasTerm && total === 0;

  // Escape vive en el contenedor: los botones de navegar/cerrar también son
  // enfocables, y con el handler solo en el input dejaba de cerrar al tabular.
  // Enter no puede subir aquí: sobre un <button> activaría "siguiente" en vez
  // de la acción del botón enfocado.
  const handleContainerKeyDown = (e) => {
    if (e.key !== "Escape") return;
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!total) return;
      if (e.shiftKey) onPrev();
      else onNext();
    }
  };

  // 44px en táctil (recorrer coincidencias es la acción principal de la barra),
  // 28px desde md, donde el puntero no necesita esa área.
  const navBtn =
    "inline-flex w-11 h-11 md:w-7 md:h-7 items-center justify-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg disabled:opacity-40 disabled:hover:bg-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500";

  return (
    <div
      id={THREAD_SEARCH_ID}
      role="search"
      onKeyDown={handleContainerKeyDown}
      className="mt-2 flex items-center gap-1.5 rounded-md border border-border bg-bg-muted/50 pl-2.5 pr-1 py-1 animate-scale-in origin-top motion-reduce:animate-none"
    >
      <Search className="w-4 h-4 text-fg-muted shrink-0" aria-hidden="true" />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={t("Buscar en esta conversación…")}
        aria-label={t("Buscar en esta conversación")}
        className="flex-1 min-w-0 bg-transparent border-0 py-1 text-sm text-fg placeholder:text-fg-muted focus:outline-none"
      />
      {/* "Sin resultados" no es un error del usuario: se queda en fg-muted. */}
      <span
        aria-live="polite"
        className={cn(
          "shrink-0 text-[11px] tabular-nums px-1 text-fg-muted",
          empty && "italic",
        )}
      >
        {!hasTerm
          ? ""
          : empty
            ? t("Sin resultados")
            : t("{{n}} de {{total}}")
                .replace("{{n}}", ordinal)
                .replace("{{total}}", total)}
      </span>
      <button
        type="button"
        onClick={onPrev}
        disabled={!total}
        aria-label={t("Coincidencia anterior")}
        title={t("Anterior (Shift+Enter)")}
        className={navBtn}
      >
        <ChevronUp className="w-4 h-4" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={!total}
        aria-label={t("Coincidencia siguiente")}
        title={t("Siguiente (Enter)")}
        className={navBtn}
      >
        <ChevronDown className="w-4 h-4" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={onClose}
        aria-label={t("Cerrar búsqueda")}
        title={t("Cerrar (Esc)")}
        className={navBtn}
      >
        <X className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
}
